import React, { useEffect, useState } from 'react';
import { Checkbox, CheckboxGroup } from '@sabre/spark-react-core/checkbox';
import { Panel } from '@sabre/spark-react-core/panel';
import SelectInput from '@sabre/spark-react-core/select-input';
import { filter, isEmpty, map, pick } from 'lodash';
import DateRange from '../../../ui-components/date-range/date-range.jsx';
import MultiSelect from '../../../ui-components/multi-select/multi-select.jsx';
import {
   DASHBOARD_TOP_TABS_VALUE,
   NOTIFICATION_STATUS,
   SIZE,
} from '../../../utils/constants.js';
import { DateUtil } from '../../../utils/date-util.js';
import {
   getFareType,
   getLabelValuePair,
   getTransformedSeasonsList,
   getUniqueSortedValues,
} from '../app-configs-util/app-configs-util.js';
import {
   AGGREGATION_DATES,
   DASHBOARD_SEARCH_CRITERIA_KEYS,
   FARE_COMPONENTS,
   KEY_ANALYST,
} from '../charts-and-grids/charts-util.js';

const SearchCriteriaFilters = ({
   appConfigs,
   filterBy,
   commonFilters,
   recommCompetitor,
   dashboardFiltersOnChangeHandler,
}) => {
   const [filters, setFilters] = useState({});
   const [selectedFilters, setSelectedFilters] = useState({});
   const [inValidFareComponent, setInValidFareComponent] = useState('');

   const setFareComponentSelection = (selectedValue, evt) => {
      if (isEmpty(selectedValue)) {
         setInValidFareComponent(NOTIFICATION_STATUS.ERROR);
      } else {
         setInValidFareComponent('');
         dashboardFiltersOnChangeHandler(
            selectedValue,
            DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_FARE_COMPONENT,
            null,
            !isEmpty(evt.target)
         );
      }
   };

   const generateFilters = (rawFilterData) => {
      const updatedSelectedFilters = {};
      map(rawFilterData, (commonFilter, commonFilterKey) => {
         switch (commonFilterKey) {
            case DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_AGGREGATION_DATE:
               updatedSelectedFilters[commonFilterKey] = {
                  [AGGREGATION_DATES.AGGREGATION_FROM_DATE]:
                     commonFilter[AGGREGATION_DATES.AGGREGATION_FROM_DATE],
                  [AGGREGATION_DATES.AGGREGATION_TO_DATE]:
                     commonFilter[AGGREGATION_DATES.AGGREGATION_TO_DATE],
               };
               break;
            case DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_FARE_TYPE:
               updatedSelectedFilters[commonFilterKey] = getUniqueSortedValues(
                  getLabelValuePair(
                     pick(getFareType(), commonFilter),
                     null,
                     true
                  )
               );
               map(updatedSelectedFilters[commonFilterKey], (eachFilter) => {
                  eachFilter.value = Math.floor(eachFilter.value);
               });
               break;
            case DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_FARE_COMPONENT:
               updatedSelectedFilters[commonFilterKey] = getUniqueSortedValues(
                  getLabelValuePair(
                     pick(FARE_COMPONENTS, commonFilter),
                     null,
                     true
                  )
               );
               break;
            case DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_SEASON:
               updatedSelectedFilters[commonFilterKey] = filter(
                  getTransformedSeasonsList(appConfigs, {
                     isConcatAbbreviation: true,
                     isShowNoSeason: true,
                  }),
                  (season) => commonFilter.indexOf(season.value) !== -1
               );
               break;
            default:
               updatedSelectedFilters[commonFilterKey] = getUniqueSortedValues(
                  getLabelValuePair(commonFilter)
               );
               break;
         }
      });

      return updatedSelectedFilters;
   };

   useEffect(() => {
      const updatedSelectedFilters = generateFilters(commonFilters);
      setFilters(updatedSelectedFilters);
      setSelectedFilters(updatedSelectedFilters);
   }, [commonFilters]);

   useEffect(() => {
      const updatedSelectedFilters = generateFilters(filterBy);
      setSelectedFilters(updatedSelectedFilters);
   }, [filterBy]);

   return (
      <Panel className='search-criteria-filters'>
         <div className='row spark-mar-b-1'>
            <div className='col-xs-12'>
               <h4 className='spark-bold text-uppercase'>Filters</h4>
            </div>
         </div>

         {recommCompetitor === DASHBOARD_TOP_TABS_VALUE.RECOMMENDATION ? (
            <div className='row'>
               <div className='col-xs-12'>
                  <MultiSelect
                     placeHolder='Region'
                     className=''
                     key={DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_REGION}
                     options={
                        filters[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_REGION]
                     }
                     selectedValues={
                        selectedFilters[
                           DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_REGION
                        ] || []
                     }
                     multiSelectOnChangeHandler={(
                        values,
                        searchSet,
                        rowIndex,
                        options,
                        userEvent
                     ) =>
                        dashboardFiltersOnChangeHandler(
                           values,
                           DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_REGION,
                           null,
                           userEvent
                        )
                     }
                  />
               </div>
            </div>
         ) : null}

         <div className='row spark-mar-t-1'>
            <div className='col-xs-12'>
               <MultiSelect
                  placeHolder='Market'
                  className=''
                  key={DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET}
                  options={
                     filters[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET]
                  }
                  selectedValues={
                     selectedFilters[
                        DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET
                     ] || []
                  }
                  multiSelectOnChangeHandler={(
                     values,
                     searchSet,
                     rowIndex,
                     options,
                     userEvent
                  ) =>
                     dashboardFiltersOnChangeHandler(
                        values,
                        DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET,
                        null,
                        userEvent
                     )
                  }
               />
            </div>
         </div>

         <div className='row spark-mar-t-1'>
            <div className='col-xs-12 col-sm-6'>
               <MultiSelect
                  placeHolder='Origin'
                  className=''
                  key={DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET_ORIGIN}
                  options={
                     filters[
                        DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET_ORIGIN
                     ]
                  }
                  selectedValues={
                     selectedFilters[
                        DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET_ORIGIN
                     ] || []
                  }
                  multiSelectOnChangeHandler={(
                     values,
                     searchSet,
                     rowIndex,
                     options,
                     userEvent
                  ) =>
                     dashboardFiltersOnChangeHandler(
                        values,
                        DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET_ORIGIN,
                        null,
                        userEvent
                     )
                  }
               />
            </div>

            <div className='col-xs-12 col-sm-6 spark-mar-t-1--xs'>
               <MultiSelect
                  placeHolder='Destination'
                  className=''
                  key={
                     DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET_DESTINATION
                  }
                  options={
                     filters[
                        DASHBOARD_SEARCH_CRITERIA_KEYS
                           .CRITERIA_MARKET_DESTINATION
                     ]
                  }
                  selectedValues={
                     selectedFilters[
                        DASHBOARD_SEARCH_CRITERIA_KEYS
                           .CRITERIA_MARKET_DESTINATION
                     ] || []
                  }
                  multiSelectOnChangeHandler={(
                     values,
                     searchSet,
                     rowIndex,
                     options,
                     userEvent
                  ) =>
                     dashboardFiltersOnChangeHandler(
                        values,
                        DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET_DESTINATION,
                        null,
                        userEvent
                     )
                  }
               />
            </div>
         </div>

         <div className='row spark-mar-t-1'>
            <div className='col-xs-12 col-sm-6'>
               <MultiSelect
                  placeHolder='Cabin'
                  className=''
                  key={DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_CABIN_CODE}
                  options={
                     filters[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_CABIN_CODE]
                  }
                  selectedValues={
                     selectedFilters[
                        DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_CABIN_CODE
                     ] || []
                  }
                  multiSelectOnChangeHandler={(
                     values,
                     searchSet,
                     rowIndex,
                     options,
                     userEvent
                  ) =>
                     dashboardFiltersOnChangeHandler(
                        values,
                        DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_CABIN_CODE,
                        null,
                        userEvent
                     )
                  }
               />
            </div>

            <div className='col-xs-12 col-sm-6 spark-mar-t-1--xs'>
               <MultiSelect
                  placeHolder='POS'
                  className=''
                  key={DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_POS}
                  options={filters[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_POS]}
                  selectedValues={
                     selectedFilters[
                        DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_POS
                     ] || []
                  }
                  multiSelectOnChangeHandler={(
                     values,
                     searchSet,
                     rowIndex,
                     options,
                     userEvent
                  ) =>
                     dashboardFiltersOnChangeHandler(
                        values,
                        DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_POS,
                        null,
                        userEvent
                     )
                  }
               />
            </div>
         </div>

         {recommCompetitor === DASHBOARD_TOP_TABS_VALUE.COMPETITION ? (
            <>
               <div className='row spark-mar-t-1'>
                  <div className='col-xs-12 col-sm-6'>
                     <MultiSelect
                        placeHolder='RBD'
                        className=''
                        key={DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_RBD}
                        options={
                           filters[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_RBD]
                        }
                        selectedValues={
                           selectedFilters[
                              DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_RBD
                           ] || []
                        }
                        multiSelectOnChangeHandler={(
                           values,
                           searchSet,
                           rowIndex,
                           options,
                           userEvent
                        ) =>
                           dashboardFiltersOnChangeHandler(
                              values,
                              DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_RBD,
                              null,
                              userEvent
                           )
                        }
                     />
                  </div>

                  <div className='col-xs-12 col-sm-6 spark-mar-t-1--xs'>
                     <MultiSelect
                        placeHolder='Competitor'
                        className=''
                        key={
                           DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_BENCHMARK_CARRIER
                        }
                        options={
                           filters[
                              DASHBOARD_SEARCH_CRITERIA_KEYS
                                 .CRITERIA_BENCHMARK_CARRIER
                           ]
                        }
                        selectedValues={
                           selectedFilters[
                              DASHBOARD_SEARCH_CRITERIA_KEYS
                                 .CRITERIA_BENCHMARK_CARRIER
                           ] || []
                        }
                        multiSelectOnChangeHandler={(
                           values,
                           searchSet,
                           rowIndex,
                           options,
                           userEvent
                        ) =>
                           dashboardFiltersOnChangeHandler(
                              values,
                              DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_BENCHMARK_CARRIER,
                              null,
                              userEvent
                           )
                        }
                     />
                  </div>
               </div>

               <div className='row spark-mar-t-1'>
                  <div className='col-xs-12 col-sm-6'>
                     <MultiSelect
                        placeHolder='Fare Type'
                        className=''
                        key={DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_FARE_TYPE}
                        options={
                           filters[
                              DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_FARE_TYPE
                           ]
                        }
                        selectedValues={
                           selectedFilters[
                              DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_FARE_TYPE
                           ] || []
                        }
                        multiSelectOnChangeHandler={(
                           values,
                           searchSet,
                           rowIndex,
                           options,
                           userEvent
                        ) =>
                           dashboardFiltersOnChangeHandler(
                              values,
                              DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_FARE_TYPE,
                              null,
                              userEvent
                           )
                        }
                     />
                  </div>

                  <div className='col-xs-12 col-sm-6 spark-mar-t-1--xs'>
                     <MultiSelect
                        placeHolder='Seasonality'
                        className=''
                        key={DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_SEASON}
                        options={
                           filters[
                              DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_SEASON
                           ]
                        }
                        selectedValues={
                           selectedFilters[
                              DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_SEASON
                           ] || []
                        }
                        multiSelectOnChangeHandler={(
                           values,
                           searchSet,
                           rowIndex,
                           options,
                           userEvent
                        ) =>
                           dashboardFiltersOnChangeHandler(
                              values,
                              DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_SEASON,
                              null,
                              userEvent
                           )
                        }
                     />
                  </div>
               </div>

               <div className='row spark-mar-t-1'>
                  <div className='col-xs-12 col-sm-6'>
                     <MultiSelect
                        placeHolder='Tariff'
                        className=''
                        key={
                           DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_TARIFF_NUMBER
                        }
                        options={
                           filters[
                              DASHBOARD_SEARCH_CRITERIA_KEYS
                                 .CRITERIA_TARIFF_NUMBER
                           ]
                        }
                        selectedValues={
                           selectedFilters[
                              DASHBOARD_SEARCH_CRITERIA_KEYS
                                 .CRITERIA_TARIFF_NUMBER
                           ] || []
                        }
                        multiSelectOnChangeHandler={(
                           values,
                           searchSet,
                           rowIndex,
                           options,
                           userEvent
                        ) =>
                           dashboardFiltersOnChangeHandler(
                              values,
                              DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_TARIFF_NUMBER,
                              null,
                              userEvent
                           )
                        }
                     />
                  </div>

                  <div className='col-xs-12 col-sm-6 spark-mar-t-1--xs'>
                     <MultiSelect
                        placeHolder='Rule Number'
                        className=''
                        key={
                           DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_RULE_NUMBER
                        }
                        options={
                           filters[
                              DASHBOARD_SEARCH_CRITERIA_KEYS
                                 .CRITERIA_RULE_NUMBER
                           ]
                        }
                        selectedValues={
                           selectedFilters[
                              DASHBOARD_SEARCH_CRITERIA_KEYS
                                 .CRITERIA_RULE_NUMBER
                           ] || []
                        }
                        multiSelectOnChangeHandler={(
                           values,
                           searchSet,
                           rowIndex,
                           options,
                           userEvent
                        ) =>
                           dashboardFiltersOnChangeHandler(
                              values,
                              DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_RULE_NUMBER,
                              null,
                              userEvent
                           )
                        }
                     />
                  </div>
               </div>

               <div className='row spark-mar-t-1'>
                  <div className='col-xs-12 col-sm-6'>
                     <MultiSelect
                        placeHolder='Footnote Code'
                        className=''
                        key={
                           DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_FOOTNOTE_CODE
                        }
                        options={
                           filters[
                              DASHBOARD_SEARCH_CRITERIA_KEYS
                                 .CRITERIA_FOOTNOTE_CODE
                           ]
                        }
                        selectedValues={
                           selectedFilters[
                              DASHBOARD_SEARCH_CRITERIA_KEYS
                                 .CRITERIA_FOOTNOTE_CODE
                           ] || []
                        }
                        multiSelectOnChangeHandler={(
                           values,
                           searchSet,
                           rowIndex,
                           options,
                           userEvent
                        ) =>
                           dashboardFiltersOnChangeHandler(
                              values,
                              DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_FOOTNOTE_CODE,
                              null,
                              userEvent
                           )
                        }
                     />
                  </div>

                  <div className='col-xs-12 col-sm-6 spark-mar-t-1--xs'>
                     {selectedFilters[
                        DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_FARE_COMPONENT
                     ] && (
                        <SelectInput
                           key={
                              DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_FARE_COMPONENT
                           }
                           name={
                              DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_FARE_COMPONENT
                           }
                           options={
                              filters[
                                 DASHBOARD_SEARCH_CRITERIA_KEYS
                                    .CRITERIA_FARE_COMPONENT
                              ]
                                 ? filters[
                                      DASHBOARD_SEARCH_CRITERIA_KEYS
                                         .CRITERIA_FARE_COMPONENT
                                   ]
                                 : []
                           }
                           label='Fare Component'
                           defaultValue={
                              selectedFilters[
                                 DASHBOARD_SEARCH_CRITERIA_KEYS
                                    .CRITERIA_FARE_COMPONENT
                              ][0].value || ''
                           }
                           status={inValidFareComponent}
                           errorMessage={'Invalid Selection'}
                           onChange={(selectedValue, evt) =>
                              setFareComponentSelection(selectedValue, evt)
                           }
                        />
                     )}
                  </div>
               </div>
            </>
         ) : null}

         {recommCompetitor === DASHBOARD_TOP_TABS_VALUE.RECOMMENDATION ? (
            <>
               <fieldset className='row spark-mar-t-1 search-criteria-filters--market-tier'>
                  <label className='col-xs-12'>Market Tier</label>
                  <CheckboxGroup
                     name={DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET_TIER}
                     onChange={(marketTierValue, el) =>
                        dashboardFiltersOnChangeHandler(
                           marketTierValue,
                           DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET_TIER,
                           el,
                           true
                        )
                     }
                  >
                     {map(
                        filters[
                           DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET_TIER
                        ],
                        (marketTier) => {
                           return (
                              <Checkbox
                                 key={marketTier.value}
                                 label={marketTier.value}
                                 value={marketTier.value}
                                 className='col-xs-12 spark-checkbox'
                                 defaultChecked={
                                    filterBy[
                                       DASHBOARD_SEARCH_CRITERIA_KEYS
                                          .CRITERIA_MARKET_TIER
                                    ].indexOf(marketTier.value) > -1
                                 }
                              />
                           );
                        }
                     )}
                  </CheckboxGroup>
               </fieldset>

               <fieldset className='row spark-mar-t-1 search-criteria-filters--analyst'>
                  <div className='col-xs-12'>
                     <MultiSelect
                        placeHolder='Analyst'
                        className=''
                        key={DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_ANALYST}
                        options={filters[KEY_ANALYST]}
                        selectedValues={selectedFilters[KEY_ANALYST] || []}
                        multiSelectOnChangeHandler={(
                           values,
                           searchSet,
                           rowIndex,
                           options,
                           userEvent
                        ) =>
                           dashboardFiltersOnChangeHandler(
                              values,
                              KEY_ANALYST,
                              null,
                              userEvent
                           )
                        }
                     />
                  </div>
               </fieldset>
            </>
         ) : null}

         {isEmpty(
            filters[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_AGGREGATION_DATE]
         ) ? null : (
            <div className='row spark-mar-t-1'>
               <div className='col-xs-12'>
                  <label>Date</label>
                  <DateRange
                     configs={{
                        inputElementDateRangeId: 'filter-date-range',
                        format: DateUtil.DATE_FORMATS.MONTH_DAY_YEAR,
                        minDate: DateUtil.formatDate(
                           filters[
                              DASHBOARD_SEARCH_CRITERIA_KEYS
                                 .CRITERIA_AGGREGATION_DATE
                           ][AGGREGATION_DATES.AGGREGATION_FROM_DATE],
                           DateUtil.DATE_FORMATS.MONTH_DAY_YEAR
                        ),
                        maxDate: DateUtil.formatDate(
                           filters[
                              DASHBOARD_SEARCH_CRITERIA_KEYS
                                 .CRITERIA_AGGREGATION_DATE
                           ][AGGREGATION_DATES.AGGREGATION_TO_DATE],
                           DateUtil.DATE_FORMATS.MONTH_DAY_YEAR
                        ),
                        startDate: DateUtil.formatDate(
                           filterBy[
                              DASHBOARD_SEARCH_CRITERIA_KEYS
                                 .CRITERIA_AGGREGATION_DATE
                           ][AGGREGATION_DATES.AGGREGATION_FROM_DATE] ||
                              filters[
                                 DASHBOARD_SEARCH_CRITERIA_KEYS
                                    .CRITERIA_AGGREGATION_DATE
                              ][AGGREGATION_DATES.AGGREGATION_FROM_DATE],
                           DateUtil.DATE_FORMATS.MONTH_DAY_YEAR
                        ),
                        endDate: DateUtil.formatDate(
                           filterBy[
                              DASHBOARD_SEARCH_CRITERIA_KEYS
                                 .CRITERIA_AGGREGATION_DATE
                           ][AGGREGATION_DATES.AGGREGATION_TO_DATE] ||
                              filters[
                                 DASHBOARD_SEARCH_CRITERIA_KEYS
                                    .CRITERIA_AGGREGATION_DATE
                              ][AGGREGATION_DATES.AGGREGATION_TO_DATE],
                           DateUtil.DATE_FORMATS.MONTH_DAY_YEAR
                        ),
                        enableRanges: false,
                        recommCompetitor: recommCompetitor,
                        dateOnChangeHandler: (startDate, endDate) =>
                           dashboardFiltersOnChangeHandler(
                              {
                                 start: startDate,
                                 end: endDate,
                              },
                              DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_AGGREGATION_DATE,
                              null,
                              true
                           ),
                     }}
                     iconSize={SIZE.MEDIUM}
                  />
               </div>
            </div>
         )}
      </Panel>
   );
};

SearchCriteriaFilters.propTypes = {};

export default SearchCriteriaFilters;
